import type { HashMap } from "@certa/types";
import type {
  Field,
  ProcessProps,
  ReferencedAppSettingsRaw,
  Step,
  Swimlane
} from "@certa/studio-core";
import {
  ChangeAction,
  ChangeLevel,
  type ChangeDiff as DesignChangeDiff
} from "@certa/queries/queries/designAI/getInteractionFulfillment.query";
import { importRulesToStudio } from "../rule.actions";
import { dbOps } from "../../hooks/dbOps.store";
import { processModelCreator } from "../../../../models/process.model";
import { swimlaneModelCreator } from "../../../../models/swimlane.model";
import { stepModelCreator } from "../../../../models/step.model";
import { fieldModelCreator } from "../../../../models/field.model";
import {
  AIIntents,
  Intents
} from "@certa/studio-core/src/constants/activityLogs.constants";
import type {
  ZodField,
  ZodRawProcess,
  ZodStep,
  ZodSwimlane
} from "@certa/queries/schemas/studio/deployableJSON.schema";
import type { EventRule } from "@certa/studio-core/src/types/eca.types";
import {
  createEvent,
  createEventRule,
  createRule
} from "../../../../models/eca.model";
import type {
  EventRaw,
  EventRuleRaw,
  RuleRaw
} from "@certa/queries/schemas/studio/eca.schema";
import { studioState } from "../../state";
import type { ObjectTypeWorkflowKindRelationRaw } from "@certa/queries/types/appSettings.types";
import { appSettingsModelCreator } from "@certa/queries/models/appSettings.model";

const appSettingsUniqueKeys: Record<keyof ReferencedAppSettingsRaw, string> = {
  emailtemplate: "slug",
  emailattachment: "title",
  group: "name",
  businessunit: "code",
  region: "code",
  extrajson: "tag",
  workflowkind: "tag",
  workflowstatus: "tag",
  objectstatus: "tag",
  workflowpdftemplate: "tag",
  alertcategory: "tag",
  commentflagoption: "tag",
  objecttypeworkflowkindrelation: "", // no unique key
  integrationconfig: "api_type",
  integrationtoken: "token_type"
};

// Integration tokens/configs are now in regular app settings, not environment-specific

export const importJSONChanges = (props: {
  changes: DesignChangeDiff[];
  processId: string;
}) => {
  const { changes, processId } = props;
  dbOps().transaction(
    () => {
      // Storing data in buffer maps, which will be applied to DB in the end using dbOps handlers
      // Ensures retrieval of up-to-date data and maintains transaction integrity.
      const processMap: HashMap<ProcessProps> = {};
      const swimlaneMap: HashMap<Swimlane> = {};
      const stepMap: HashMap<Step> = {};
      const fieldMap: HashMap<Field> = {};
      const eventRuleMap: HashMap<EventRule> = {};
      const eventRuleChanges: {
        delete: DesignChangeDiff[];
        create: DesignChangeDiff[];
        update: DesignChangeDiff[];
      } = {
        delete: [],
        create: [],
        update: []
      };
      let appSettings = studioState().getAppSettingsRaw();

      // Getters to get data from buffer maps or DB if not found
      const getProcessFromMapOrDB = () => {
        const process = processMap[processId] || dbOps().getProcess(processId);

        if (!process) {
          throw new Error(
            `Process not found in studio. Process id: ${processId}`
          );
        }

        processMap[processId] = process;
        return process;
      };

      const getSwimlaneByTag = (swimlaneTag: string) => {
        const swimlane =
          swimlaneMap[swimlaneTag] ||
          dbOps().getSwimlaneByTag(processId, swimlaneTag);

        if (!swimlane) {
          throw new Error(
            `Swimlane not found in studio. Swimlane tag: ${swimlaneTag}. Current swimlane map tags: ${JSON.stringify(
              Object.values(swimlaneMap).map(swimlane => swimlane.tag)
            )} and studio swimlanes: ${JSON.stringify(
              dbOps()
                .getSwimlanesOfProcess(processId)
                .map(swimlane => swimlane.tag)
            )}`
          );
        }

        swimlaneMap[swimlaneTag] = swimlane;
        return swimlane;
      };

      const getStepByTag = (stepTag: string) => {
        const step =
          stepMap[stepTag] || dbOps().getStepByTag(processId, stepTag);

        if (!step) {
          throw new Error(
            `Step not found in studio. Step tag: ${stepTag}. Current step map tags: ${JSON.stringify(
              Object.values(stepMap).map(step => step.tag)
            )} and studio steps: ${JSON.stringify(
              dbOps()
                .getStepsOfProcess(processId)
                .map(step => step.tag)
            )}`
          );
        }

        stepMap[stepTag] = step;
        return step;
      };

      const getFieldByTag = (fieldTag: string) => {
        const field =
          fieldMap[fieldTag] || dbOps().getFieldByTag(processId, fieldTag);

        if (!field) {
          throw new Error(
            `Field not found in studio. Field tag: ${fieldTag}. Current field map tags: ${JSON.stringify(
              Object.values(fieldMap).map(field => field.tag)
            )} and studio fields: ${JSON.stringify(
              dbOps()
                .getFieldsOfProcess(processId)
                .map(field => field.tag)
            )}`
          );
        }

        fieldMap[fieldTag] = field;
        return field;
      };

      const getSwimlaneByStepId = (stepId: string) => {
        let swimlane = Object.values(swimlaneMap).find(swimlane =>
          swimlane.steps.includes(stepId)
        );

        if (!swimlane) {
          swimlane = dbOps().getSwimlaneByStepId(processId, stepId);
          if (!swimlane) {
            throw new Error(
              `Swimlane not found in studio. Step id: ${stepId}. Current swimlane map: ${JSON.stringify(swimlaneMap)}`
            );
          }

          swimlaneMap[swimlane.tag] = swimlane;
        }

        return swimlane;
      };

      const getStepByFieldId = (fieldId: string) => {
        let step = Object.values(stepMap).find(step =>
          step.fields.includes(fieldId)
        );

        if (!step) {
          step = dbOps().getStepByFieldId(processId, fieldId);
          if (!step) {
            throw new Error(
              `Step not found in studio. Field id: ${fieldId}. Current step map: ${JSON.stringify(stepMap)}`
            );
          }

          stepMap[step.tag] = step;
        }

        return step;
      };
      const getEventRuleById = (eventRuleId: string) => {
        const eventRule =
          eventRuleMap[eventRuleId] ||
          dbOps().getEventRule(processId, eventRuleId);
        if (!eventRule) {
          throw new Error(
            `Event rule with id ${eventRuleId} not found in studio. Current event rule map: ${JSON.stringify(eventRuleMap)} and studio event rules: ${JSON.stringify(dbOps().getEventRules(processId))}`
          );
        }

        return eventRule;
      };
      const getEventRuleByIndex = (eventRuleIndex: number) => {
        const eventRuleId =
          getProcessFromMapOrDB().eventRuleSequence[eventRuleIndex];
        if (!eventRuleId) {
          throw new Error(
            `Event rule id with index ${eventRuleIndex} not found in studio or Map. Current event rule sequence: ${JSON.stringify(getProcessFromMapOrDB().eventRuleSequence)}`
          );
        }
        return getEventRuleById(eventRuleId);
      };
      const getEventRuleByIndexFromDB = (eventRuleIndex: number) => {
        const process = dbOps().getProcess(processId);
        if (!process) {
          throw new Error(
            `Process not found in studio. Process id: ${processId}`
          );
        }
        const eventRuleId = process.eventRuleSequence[eventRuleIndex];
        if (!eventRuleId) {
          throw new Error(
            `Event rule id with index ${eventRuleIndex} not found in studio. Current event rule sequence: ${JSON.stringify(process.eventRuleSequence)}`
          );
        }
        return getEventRuleById(eventRuleId);
      };

      const updateEventRuleMap = (eventRule: EventRule) => {
        eventRuleMap[eventRule.__studioId] = eventRule;
      };

      // Apply changes
      for (const change of changes) {
        // =================== PROCESS ===================
        if (change.level === "process") {
          /** Gets process MetaJSON of process DeployableJSON Diff */
          const getChangedProcessMetadata = () => {
            if (change.data) {
              return processModelCreator(
                { ...change.data, stepgroups: {} } as ZodRawProcess,
                {
                  processId
                }
              );
            } else {
              throw new Error(
                "Data not provided while importing process change."
              );
            }
          };

          // UPDATE
          if (change.action === "update") {
            const processFromDB = getProcessFromMapOrDB();

            if (change.data) {
              const { process } = getChangedProcessMetadata();
              const newProcess: ProcessProps = { ...process };

              // Copy previous values of keys that shouldn't be updated
              newProcess.id = processFromDB.id;
              newProcess.kind = {
                ...newProcess.kind,
                tag: processFromDB.kind.tag
              };

              newProcess.swimlaneSequence = processFromDB.swimlaneSequence;
              newProcess.__createdAt = processFromDB.__createdAt;
              newProcess.__branchTimestamp = processFromDB.__branchTimestamp;
              newProcess.__saveId = processFromDB.__saveId;
              newProcess.eventRuleSequence = processFromDB.eventRuleSequence;
              newProcess.stateRuleSequence = processFromDB.stateRuleSequence;

              processMap[processId] = newProcess;
            }
          }
        }

        // =================== SWIMLANE ===================
        else if (change.level === "swimlane") {
          /** Gets swimlane MetaJSON of swimlane DeployableJSON Diff */
          const getChangedSwimlaneMetadata = () => {
            if (change.data) {
              return swimlaneModelCreator(
                { ...change.data, steps: {} } as ZodSwimlane,
                {
                  processId
                }
              );
            } else {
              throw new Error(
                "Data not provided while importing swimlane change."
              );
            }
          };

          // Get parent component
          const parentProcess = getProcessFromMapOrDB();

          // CREATE
          if (change.action === "create") {
            const { swimlane, ruleMap, conditionGroupMap, conditionMap } =
              getChangedSwimlaneMetadata();

            // Insert new component at correct position
            if (change.atIndex !== undefined) {
              parentProcess.swimlaneSequence.splice(
                change.atIndex,
                0,
                swimlane.id
              );
            } else {
              parentProcess.swimlaneSequence.push(swimlane.id);
            }

            processMap[processId] = parentProcess;
            swimlaneMap[swimlane.tag] = swimlane;
            importRulesToStudio({
              processId,
              ruleMap,
              conditionGroupMap,
              conditionMap
            });
          }

          // UPDATE
          else if (change.action === "update") {
            const swimlaneFromDB = getSwimlaneByTag(change.tag);

            if (change.data) {
              const { swimlane, ruleMap, conditionGroupMap, conditionMap } =
                getChangedSwimlaneMetadata();

              const newSwimlane: Swimlane = { ...swimlane };

              // Copy previous values of keys that shouldn't be updated
              newSwimlane.id = swimlaneFromDB.id;
              newSwimlane.tag = swimlaneFromDB.tag;
              newSwimlane.steps = swimlaneFromDB.steps;
              newSwimlane.__createdAt = swimlaneFromDB.__createdAt;

              swimlaneMap[swimlane.tag] = newSwimlane;
              importRulesToStudio({
                processId,
                ruleMap,
                conditionGroupMap,
                conditionMap
              });
            }

            // Reposition
            if (change.atIndex !== undefined) {
              // Check if repositioning in same parent component
              const swimlaneIndex = parentProcess.swimlaneSequence.findIndex(
                id => id === swimlaneFromDB.id
              );
              if (swimlaneIndex === -1) {
                throw new Error(
                  `Swimlane not found on parent process. Current swimlane sequence: ${JSON.stringify(
                    parentProcess.swimlaneSequence
                  )}`
                );
              }

              // Update position in same parent
              parentProcess.swimlaneSequence.splice(swimlaneIndex, 1);
              parentProcess.swimlaneSequence.splice(
                change.atIndex,
                0,
                swimlaneFromDB.id
              );

              processMap[processId] = parentProcess;
            }
          }

          // DELETE
          else if (change.action === "delete") {
            const swimlaneFromDB = getSwimlaneByTag(change.tag);

            // Delete from buffer map
            delete swimlaneMap[swimlaneFromDB.tag];
            // Run cascading deletes from DB
            dbOps().removeSwimlane(processId, swimlaneFromDB.id);

            // Remove component from parent
            const swimlaneIndex = parentProcess.swimlaneSequence.findIndex(
              id => id === swimlaneFromDB.id
            );
            if (swimlaneIndex === -1) {
              continue;
            }
            parentProcess.swimlaneSequence.splice(swimlaneIndex, 1);

            processMap[processId] = parentProcess;
          }
        }

        // =================== STEP ===================
        else if (change.level === "step") {
          /** Gets step MetaJSON of step DeployableJSON Diff */
          const getChangedStepMetadata = () => {
            if (change.data) {
              return stepModelCreator(
                { ...change.data, fields: {} } as ZodStep,
                {
                  processId
                }
              );
            } else {
              throw new Error("Data not provided while importing step change.");
            }
          };

          // Get parent component
          let parentSwimlane: Swimlane | undefined;
          if (change.parentTag) {
            parentSwimlane = getSwimlaneByTag(change.parentTag);
          } else {
            const stepFromDB = getStepByTag(change.tag);

            parentSwimlane = getSwimlaneByStepId(stepFromDB.id);
          }
          if (!parentSwimlane)
            throw new Error(
              `Swimlane not found while importing step. Current step tag: ${change.tag}. Current step map: ${JSON.stringify(stepMap)}`
            );

          // CREATE
          if (change.action === "create") {
            const { step, ruleMap, conditionGroupMap, conditionMap } =
              getChangedStepMetadata();

            // Insert new component at correct position
            if (change.atIndex !== undefined) {
              parentSwimlane.steps.splice(change.atIndex, 0, step.id);
            } else {
              parentSwimlane.steps.push(step.id);
            }

            stepMap[step.tag] = step;
            swimlaneMap[parentSwimlane.tag] = parentSwimlane;
            importRulesToStudio({
              processId,
              ruleMap,
              conditionGroupMap,
              conditionMap
            });
          }

          // UPDATE
          else if (change.action === "update") {
            const stepFromDB = getStepByTag(change.tag);

            if (change.data) {
              const { step, ruleMap, conditionGroupMap, conditionMap } =
                getChangedStepMetadata();

              const newStep: Step = { ...step };

              // Remove keys that shouldn't be updated
              newStep.id = stepFromDB.id;
              newStep.tag = stepFromDB.tag;
              newStep.fields = stepFromDB.fields;
              newStep.__createdAt = stepFromDB.__createdAt;

              stepMap[step.tag] = newStep;
              importRulesToStudio({
                processId,
                ruleMap,
                conditionGroupMap,
                conditionMap
              });
            }

            // Reposition
            if (change.atIndex !== undefined && change.parentTag) {
              // Check if repositioning in same parent component
              const stepIndex = parentSwimlane.steps.findIndex(
                id => id === stepFromDB.id
              );
              if (stepIndex === -1) {
                // Remove component from old parent
                const oldSwimlane = getSwimlaneByStepId(stepFromDB.id);
                if (!oldSwimlane) {
                  throw new Error(
                    "Old swimlane not found while repositioning step."
                  );
                }
                const stepIndex = oldSwimlane.steps.findIndex(
                  id => id === stepFromDB.id
                );
                if (stepIndex === -1) {
                  throw new Error(
                    "Step not found in old swimlane while repositioning step."
                  );
                }
                oldSwimlane.steps.splice(stepIndex, 1);
                swimlaneMap[oldSwimlane.tag] = oldSwimlane;

                // Add component to new parent
                parentSwimlane.steps.splice(change.atIndex, 0, stepFromDB.id);
                swimlaneMap[parentSwimlane.tag] = parentSwimlane;
              } else {
                // Update position in same parent
                parentSwimlane.steps.splice(stepIndex, 1);
                parentSwimlane.steps.splice(change.atIndex, 0, stepFromDB.id);

                swimlaneMap[parentSwimlane.tag] = parentSwimlane;
              }
            }
          }

          // DELETE
          else if (change.action === "delete") {
            const stepFromDB = getStepByTag(change.tag);

            // Delete from buffer map
            delete stepMap[stepFromDB.tag];
            // Cascasding deletes from DB
            dbOps().removeStep(processId, stepFromDB.id);

            // Remove component from parent
            const stepIndex = parentSwimlane.steps.findIndex(
              id => id === stepFromDB.id
            );
            if (stepIndex === -1) continue;
            parentSwimlane.steps.splice(stepIndex, 1);
            swimlaneMap[parentSwimlane.tag] = parentSwimlane;
          }
        }

        // =================== FIELD ===================
        else if (change.level === "field") {
          /** Gets field MetaJSON of field DeployableJSON  */
          const getChangedFieldMetadata = () => {
            if (change.data) {
              return fieldModelCreator(change.data as ZodField, {
                processId
              });
            } else {
              throw new Error(
                "Data not provided while importing field change."
              );
            }
          };

          // Get parent component
          let parentStep: Step | undefined;
          if (change.parentTag) {
            parentStep = getStepByTag(change.parentTag);
          } else {
            const fieldFromDB = getFieldByTag(change.tag);

            parentStep = getStepByFieldId(fieldFromDB.id);
          }
          if (!parentStep)
            throw new Error("Step not found while importing field.");

          // CREATE
          if (change.action === "create") {
            const { field, ruleMap, conditionGroupMap, conditionMap } =
              getChangedFieldMetadata();

            // Insert new component at correct position
            if (change.atIndex !== undefined) {
              parentStep.fields.splice(change.atIndex, 0, field.id);
            } else {
              parentStep.fields.push(field.id);
            }

            fieldMap[field.tag] = field;
            stepMap[parentStep.tag] = parentStep;
            importRulesToStudio({
              processId,
              ruleMap,
              conditionGroupMap,
              conditionMap
            });
          }

          // UPDATE
          else if (change.action === "update") {
            const fieldFromDB = getFieldByTag(change.tag);

            if (change.data) {
              const { field, ruleMap, conditionGroupMap, conditionMap } =
                getChangedFieldMetadata();

              const newField: Field = { ...field };

              // Remove keys that shouldn't be updated
              newField.id = fieldFromDB.id;
              newField.tag = fieldFromDB.tag;
              newField.__createdAt = fieldFromDB.__createdAt;

              fieldMap[field.tag] = newField;
              importRulesToStudio({
                processId,
                ruleMap,
                conditionGroupMap,
                conditionMap
              });
            }

            // Reposition
            if (change.atIndex !== undefined && change.parentTag) {
              // Check if repositioning in same parent component
              const fieldIndex = parentStep.fields.findIndex(
                id => id === fieldFromDB.id
              );

              if (fieldIndex === -1) {
                // Remove component from old parent
                const oldStep = getStepByFieldId(fieldFromDB.id);
                if (!oldStep)
                  throw new Error(
                    "Old step not found while repositioning field."
                  );
                const fieldIndex = oldStep.fields.findIndex(
                  id => id === fieldFromDB.id
                );
                if (fieldIndex === -1) {
                  throw new Error(
                    "Field not found in old step while repositioning field."
                  );
                }
                oldStep.fields.splice(fieldIndex, 1);
                stepMap[oldStep.tag] = oldStep;

                // Add component to new parent
                parentStep.fields.splice(change.atIndex, 0, fieldFromDB.id);
                stepMap[parentStep.tag] = parentStep;
              } else {
                // Update position in same parent
                parentStep.fields.splice(fieldIndex, 1);
                parentStep.fields.splice(change.atIndex, 0, fieldFromDB.id);

                stepMap[parentStep.tag] = parentStep;
              }
            }
          }

          // DELETE
          else if (change.action === "delete") {
            const fieldFromDB = getFieldByTag(change.tag);

            // Delete from buffer map
            delete fieldMap[fieldFromDB.tag];
            // Cascading deletes from DB
            dbOps().removeField(processId, fieldFromDB.id);

            // Remove component from parent
            const fieldIndex = parentStep.fields.findIndex(
              id => id === fieldFromDB.id
            );
            if (fieldIndex === -1) continue;

            parentStep.fields.splice(fieldIndex, 1);
            stepMap[parentStep.tag] = parentStep;
          }
        } else if (change.level === "event_rules") {
          if (change.path) {
            if (change.action === "create") {
              eventRuleChanges.create.push(change);
            } else if (change.action === "update") {
              eventRuleChanges.update.push(change);
            } else if (change.action === "delete") {
              eventRuleChanges.delete.push(change);
            }
          }
        }

        // =================== APP SETTINGS ===================
        else if (change.level === "app_settings") {
          if (change.action === "create") {
            const settingKey = change.setting as keyof ReferencedAppSettingsRaw;

            appSettings = {
              ...appSettings,
              [settingKey]: [...(appSettings[settingKey] ?? []), change.data]
            };
          } else if (change.action === "update") {
            const settingKey = change.setting as keyof ReferencedAppSettingsRaw;
            const uniqueId = change.uniqueId as string;
            const uniqueKey = appSettingsUniqueKeys[settingKey];

            if (settingKey === "objecttypeworkflowkindrelation") {
              const [objectType, workflowKind, relationType] = uniqueId.split(
                "--"
              ) as [string, string, string];
              appSettings = {
                ...appSettings,
                objecttypeworkflowkindrelation:
                  appSettings.objecttypeworkflowkindrelation.map(objWFRel =>
                    objWFRel.object_type === objectType &&
                    objWFRel.workflow_kind === workflowKind &&
                    objWFRel.relation === relationType
                      ? (change.data as ObjectTypeWorkflowKindRelationRaw)
                      : objWFRel
                  )
              };
            } else {
              appSettings = {
                ...appSettings,
                [settingKey]: (appSettings[settingKey] ?? []).map(appSetting =>
                  appSetting[uniqueKey as keyof typeof appSetting] === uniqueId
                    ? change.data
                    : appSetting
                )
              };
            }
          } else if (change.action === "delete") {
            const settingKey = change.setting as keyof ReferencedAppSettingsRaw;
            const uniqueId = change.uniqueId as string;
            const uniqueKey = appSettingsUniqueKeys[settingKey];

            if (settingKey === "objecttypeworkflowkindrelation") {
              const [objectType, workflowKind, relationType] = uniqueId.split(
                "--"
              ) as [string, string, string];
              appSettings = {
                ...appSettings,
                objecttypeworkflowkindrelation:
                  appSettings.objecttypeworkflowkindrelation.filter(
                    objWFRel =>
                      objWFRel.object_type !== objectType ||
                      objWFRel.workflow_kind !== workflowKind ||
                      objWFRel.relation !== relationType
                  )
              };
            } else {
              appSettings = {
                ...appSettings,
                [settingKey]: (appSettings[settingKey] ?? []).filter(
                  appSetting =>
                    appSetting[uniqueKey as keyof typeof appSetting] !==
                    uniqueId
                )
              };
            }
          }
        }
      }

      const hasEventRuleCreateChanges = eventRuleChanges.create.length > 0;
      const hasEventRuleUpdateChanges = eventRuleChanges.update.length > 0;
      const hasEventRuleDeleteChanges = eventRuleChanges.delete.length > 0;

      if (
        hasEventRuleCreateChanges ||
        hasEventRuleUpdateChanges ||
        hasEventRuleDeleteChanges
      ) {
        const getChangedEventRuleMetadata = (
          changeData: HashMap | undefined
        ) => {
          if (changeData) {
            return createEventRule({ ...changeData } as EventRuleRaw);
          } else {
            throw new Error(
              "Data not provided while importing event rule change."
            );
          }
        };
        const getChangedEventRuleRuleMetadata = (
          changeData: HashMap | undefined
        ) => {
          if (changeData) {
            return createRule({ ...changeData } as RuleRaw);
          } else {
            throw new Error(
              "Data not provided while importing event rule's rule change."
            );
          }
        };
        const getChangedEventRuleEventMetadata = (
          changeData: HashMap | undefined
        ) => {
          if (changeData) {
            return createEvent({ ...changeData } as EventRaw);
          } else {
            throw new Error(
              "Data not provided while importing event rule's event change."
            );
          }
        };

        if (hasEventRuleDeleteChanges) {
          // sort by path's index in reverse order so that we delete from the end
          eventRuleChanges.delete
            .sort(sortEventRuleChangesByPathReverse)
            .forEach(change => {
              const eventRuleIndex = getEventRuleIndex(change.path);
              const isChangeAtEventRule = getIsChangeAtEventRule(change.path);
              const isChangeAtEventRuleRule = getIsChangeAtEventRuleRule(
                change.path
              );
              const isChangeAtEventRuleEvent = getIsChangeAtEventRuleEvent(
                change.path
              );
              if (eventRuleIndex === undefined) {
                throw new Error(
                  `Event rule index not found in path: ${change.path}`
                );
              }
              // index that we get from path is from the sequence which is not updated yet
              const currentEventRule =
                getEventRuleByIndexFromDB(eventRuleIndex);
              if (isChangeAtEventRule) {
                delete eventRuleMap[currentEventRule.__studioId];
                const process = getProcessFromMapOrDB();
                process.eventRuleSequence.splice(eventRuleIndex, 1);
                processMap[processId] = process;
                dbOps().removeEventRule(processId, currentEventRule.__studioId);
              } else if (isChangeAtEventRuleRule) {
                const eventRuleRuleIndex = getEventRuleRuleIndex(change.path);
                if (eventRuleRuleIndex === undefined) {
                  throw new Error(
                    `Event rule's rule index not found in path: ${change.path}`
                  );
                }
                const isRuleIndexWrong =
                  currentEventRule.rules[eventRuleRuleIndex] === undefined;
                if (isRuleIndexWrong) {
                  throw new Error(
                    "Trying to delete a rule that doesn't exist: " +
                      eventRuleRuleIndex +
                      ". Current event rule rules: " +
                      JSON.stringify(currentEventRule.rules)
                  );
                }
                currentEventRule.rules.splice(eventRuleRuleIndex, 1);
                updateEventRuleMap(currentEventRule);
              } else if (isChangeAtEventRuleEvent) {
                const eventRuleEventIndex = getEventRuleEventIndex(change.path);
                if (eventRuleEventIndex === undefined) {
                  throw new Error(
                    `Event rule's event index not found in path: ${change.path}`
                  );
                }
                const isEventIndexWrong =
                  currentEventRule.events[eventRuleEventIndex] === undefined;
                if (isEventIndexWrong) {
                  throw new Error(
                    "Trying to delete an event that doesn't exist: " +
                      eventRuleEventIndex +
                      ". Current event rule events: " +
                      JSON.stringify(currentEventRule.events)
                  );
                }
                currentEventRule.events.splice(eventRuleEventIndex, 1);
                updateEventRuleMap(currentEventRule);
              }
            });
        }
        if (hasEventRuleCreateChanges) {
          eventRuleChanges.create.forEach(change => {
            const eventRuleIndex = getEventRuleIndex(change.path);
            const isChangeAtEventRule = getIsChangeAtEventRule(change.path);
            const isChangeAtEventRuleRule = getIsChangeAtEventRuleRule(
              change.path
            );
            const isChangeAtEventRuleEvent = getIsChangeAtEventRuleEvent(
              change.path
            );
            if (isChangeAtEventRule) {
              if (eventRuleIndex === undefined) {
                throw new Error(
                  "Event rule index not found in path: " + change.path
                );
              }
              const eventRule = getChangedEventRuleMetadata(change.data);
              updateEventRuleMap(eventRule);

              const process = getProcessFromMapOrDB();
              const isEventRuleIndexWrong =
                process.eventRuleSequence.length !== eventRuleIndex ||
                process.eventRuleSequence[eventRuleIndex] !== undefined;
              if (isEventRuleIndexWrong) {
                throw new Error(
                  "Trying to create an event rule at wrong index: " +
                    eventRuleIndex +
                    ". Current event rule sequence: " +
                    JSON.stringify(process.eventRuleSequence)
                );
              }
              process.eventRuleSequence[eventRuleIndex] = eventRule.__studioId;
              processMap[processId] = process;
            } else if (
              isChangeAtEventRuleRule &&
              eventRuleIndex !== undefined
            ) {
              const eventRuleRuleIndex = getEventRuleRuleIndex(change.path);
              if (eventRuleRuleIndex === undefined) {
                throw new Error(
                  "Event rule's rule index not found in path: " + change.path
                );
              }
              const eventRuleRule = getChangedEventRuleRuleMetadata(
                change.data
              );
              const eventRule = getEventRuleByIndex(eventRuleIndex);
              const isRuleIndexWrong =
                eventRule.rules.length !== eventRuleRuleIndex ||
                eventRule.rules[eventRuleRuleIndex] !== undefined;
              if (isRuleIndexWrong) {
                throw new Error(
                  "Trying to create a rule at wrong index: " +
                    eventRuleRuleIndex +
                    ". Current event rule rules: " +
                    JSON.stringify(eventRule.rules)
                );
              }
              eventRule.rules[eventRuleRuleIndex] = eventRuleRule;
              updateEventRuleMap(eventRule);
            } else if (
              isChangeAtEventRuleEvent &&
              eventRuleIndex !== undefined
            ) {
              const eventRuleEventIndex = getEventRuleEventIndex(change.path);
              if (eventRuleEventIndex === undefined) {
                throw new Error(
                  "Event rule's event index not found in path: " + change.path
                );
              }
              const eventRuleEvent = getChangedEventRuleEventMetadata(
                change.data
              );
              const eventRule = getEventRuleByIndex(eventRuleIndex);
              const isEventIndexWrong =
                eventRule.events.length !== eventRuleEventIndex ||
                eventRule.events[eventRuleEventIndex] !== undefined;
              if (isEventIndexWrong) {
                throw new Error(
                  "Trying to create an event at wrong index: " +
                    eventRuleEventIndex +
                    ". Current event rule events: " +
                    JSON.stringify(eventRule.events)
                );
              }
              eventRule.events[eventRuleEventIndex] = eventRuleEvent;
              updateEventRuleMap(eventRule);
            }
          });
        }
        if (hasEventRuleUpdateChanges) {
          eventRuleChanges.update.forEach(change => {
            const eventRuleIndex = getEventRuleIndex(change.path);
            const isChangeAtEventRule = getIsChangeAtEventRule(change.path);
            const isChangeAtEventRuleRule = getIsChangeAtEventRuleRule(
              change.path
            );
            const isChangeAtEventRuleEvent = getIsChangeAtEventRuleEvent(
              change.path
            );

            if (eventRuleIndex === undefined) {
              throw new Error(
                `Event rule index not found in path: ${change.path}`
              );
            }
            const currentEventRule = getEventRuleByIndex(eventRuleIndex);
            if (isChangeAtEventRule) {
              const eventRule = getChangedEventRuleMetadata(change.data);
              // Use current event rule id instead of new one
              eventRule.__studioId = currentEventRule.__studioId;
              updateEventRuleMap(eventRule);
            } else if (isChangeAtEventRuleRule) {
              const eventRuleRule = getChangedEventRuleRuleMetadata(
                change.data
              );
              const eventRuleRuleIndex = getEventRuleRuleIndex(change.path);
              if (eventRuleRuleIndex === undefined) {
                throw new Error(
                  `Event rule's rule index not found in path: ${change.path}`
                );
              }
              const isRuleIndexWrong =
                currentEventRule.rules[eventRuleRuleIndex] === undefined;
              if (isRuleIndexWrong) {
                throw new Error(
                  "Trying to update a rule that doesn't exist: " +
                    eventRuleRuleIndex +
                    ". Current event rule rules: " +
                    JSON.stringify(currentEventRule.rules)
                );
              }
              currentEventRule.rules[eventRuleRuleIndex] = eventRuleRule;
              updateEventRuleMap(currentEventRule);
            } else if (isChangeAtEventRuleEvent) {
              const eventRuleEvent = getChangedEventRuleEventMetadata(
                change.data
              );
              const eventRuleEventIndex = getEventRuleEventIndex(change.path);
              if (eventRuleEventIndex === undefined) {
                throw new Error(
                  `Event rule's event index not found in path: ${change.path}`
                );
              }
              const isEventIndexWrong =
                currentEventRule.events[eventRuleEventIndex] === undefined;
              if (isEventIndexWrong) {
                throw new Error(
                  "Trying to update an event that doesn't exist: " +
                    eventRuleEventIndex +
                    ". Current event rule events: " +
                    JSON.stringify(currentEventRule.events)
                );
              }
              currentEventRule.events[eventRuleEventIndex] = eventRuleEvent;
              updateEventRuleMap(currentEventRule);
            }
          });
        }
      }

      // Update all accumulated changes via dbOps
      Object.values(processMap).forEach(process =>
        dbOps().setProcess(processId, process)
      );
      Object.values(swimlaneMap).forEach(swimlane =>
        dbOps().setSwimlane(processId, swimlane)
      );
      Object.values(stepMap).forEach(step => dbOps().setStep(processId, step));
      Object.values(fieldMap).forEach(field =>
        dbOps().setField(processId, field)
      );
      Object.values(stepMap).forEach(step => dbOps().setStep(processId, step));
      Object.values(fieldMap).forEach(field =>
        dbOps().setField(processId, field)
      );
      Object.values(eventRuleMap).forEach(eventRule =>
        dbOps().setEventRule(processId, eventRule)
      );
      dbOps().replaceAllAppSettings(
        appSettingsModelCreator({
          ...appSettings,
          file_paths: []
        })
      );
    },
    {
      aiIntents: changes.map(change => getAIIntent(change)),
      intent: Intents.DESIGN_AI_PROMPT
    }
  );
};

const aiIntentMapper: Record<ChangeAction, Record<ChangeLevel, AIIntents>> = {
  [ChangeAction.CREATE]: {
    [ChangeLevel.PROCESS]: AIIntents.CREATE_PROCESS,
    [ChangeLevel.SWIMLANE]: AIIntents.CREATE_SWIMLANE,
    [ChangeLevel.STEP]: AIIntents.CREATE_STEP,
    [ChangeLevel.FIELD]: AIIntents.CREATE_FIELD,
    [ChangeLevel.EVENT_RULES]: AIIntents.CREATE_EVENT_RULE,
    [ChangeLevel.APP_SETTINGS]: AIIntents.CREATE_APP_SETTINGS,
    [ChangeLevel.RECORDS]: AIIntents.CREATE_DATA_OBJECT
  },
  [ChangeAction.UPDATE]: {
    [ChangeLevel.PROCESS]: AIIntents.UPDATE_PROCESS,
    [ChangeLevel.SWIMLANE]: AIIntents.UPDATE_SWIMLANE,
    [ChangeLevel.STEP]: AIIntents.UPDATE_STEP,
    [ChangeLevel.FIELD]: AIIntents.UPDATE_FIELD,
    [ChangeLevel.EVENT_RULES]: AIIntents.UPDATE_EVENT_RULE,
    [ChangeLevel.APP_SETTINGS]: AIIntents.UPDATE_APP_SETTINGS,
    [ChangeLevel.RECORDS]: AIIntents.UPDATE_DATA_OBJECT
  },
  [ChangeAction.DELETE]: {
    [ChangeLevel.PROCESS]: AIIntents.DELETE_PROCESS,
    [ChangeLevel.SWIMLANE]: AIIntents.DELETE_SWIMLANE,
    [ChangeLevel.STEP]: AIIntents.DELETE_STEP,
    [ChangeLevel.FIELD]: AIIntents.DELETE_FIELD,
    [ChangeLevel.EVENT_RULES]: AIIntents.DELETE_EVENT_RULE,
    [ChangeLevel.APP_SETTINGS]: AIIntents.DELETE_APP_SETTINGS,
    [ChangeLevel.RECORDS]: AIIntents.DELETE_DATA_OBJECT
  }
};

const getAIIntent = (change: DesignChangeDiff) => {
  const intent = aiIntentMapper[change.action][change.level];
  if (change.path) {
    const isChangeAtEventRule = getIsChangeAtEventRule(change.path);
    const isChangeAtEventRuleRule = getIsChangeAtEventRuleRule(change.path);
    const isChangeAtEventRuleEvent = getIsChangeAtEventRuleEvent(change.path);

    if (intent === AIIntents.CREATE_EVENT_RULE) {
      if (isChangeAtEventRule) {
        return AIIntents.CREATE_EVENT_RULE;
      } else if (isChangeAtEventRuleRule) {
        return AIIntents.CREATE_EVENT_RULE_RULE;
      } else if (isChangeAtEventRuleEvent) {
        return AIIntents.CREATE_EVENT_RULE_EVENT;
      }
    } else if (intent === AIIntents.UPDATE_EVENT_RULE) {
      if (isChangeAtEventRule) {
        return AIIntents.UPDATE_EVENT_RULE;
      } else if (isChangeAtEventRuleRule) {
        return AIIntents.UPDATE_EVENT_RULE_RULE;
      } else if (isChangeAtEventRuleEvent) {
        return AIIntents.UPDATE_EVENT_RULE_EVENT;
      }
    } else if (intent === AIIntents.DELETE_EVENT_RULE) {
      if (isChangeAtEventRule) {
        return AIIntents.DELETE_EVENT_RULE;
      } else if (isChangeAtEventRuleRule) {
        return AIIntents.DELETE_EVENT_RULE_RULE;
      } else if (isChangeAtEventRuleEvent) {
        return AIIntents.DELETE_EVENT_RULE_EVENT;
      }
    }
  }
  return intent;
};

const getIsChangeAtEventRuleRule = (path: string | undefined) => {
  if (!path) {
    return false;
  }
  // Match pattern like event_rules[0].rules[1]
  return /^event_rules\[\d+\]\.rules\[\d+\]/.test(path);
};
const getIsChangeAtEventRuleEvent = (path: string | undefined) => {
  if (!path) {
    return false;
  }
  return /^event_rules\[\d+\]\.events\[\d+\]/.test(path);
};
const getIsChangeAtEventRule = (path: string | undefined) => {
  return !(
    getIsChangeAtEventRuleRule(path) || getIsChangeAtEventRuleEvent(path)
  );
};

const getEventRuleIndex = (path: string | undefined) => {
  if (!path) {
    return undefined;
  }
  const match = path.match(/event_rules\[(\d+)\]/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return undefined;
};

const getEventRuleRuleIndex = (path: string | undefined) => {
  if (!path) {
    return undefined;
  }
  // Match pattern like event_rules[0].rules[1]
  const match = path.match(/^event_rules\[\d+\]\.rules\[(\d+)\]/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return undefined;
};

const getEventRuleEventIndex = (path: string | undefined) => {
  if (!path) {
    return undefined;
  }
  // Match pattern like event_rules[0].events[1]
  const match = path.match(/^event_rules\[\d+\]\.events\[(\d+)\]/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return undefined;
};

/**
 * Comparator function for sorting event rule changes by their path indices in reverse order.
 * This ensures deletion happens from the end to avoid index shifting issues.
 *
 * @param changeA First change object with a path property
 * @param changeB Second change object with a path property
 * @returns Comparison result for Array.sort() - positive if A should come after B
 *
 * @example
 * input:
 * [
        { path: "event_rules[0].rules[0]" },
        { path: "event_rules[0]" },
        { path: "event_rules[0].events[0]" },
        { path: "event_rules[1].rules[0]" },
        { path: "event_rules[1]" }
      ]
 * output:
 * [
        "event_rules[1].rules[0]",
        "event_rules[1]",
        "event_rules[0].rules[0]",
        "event_rules[0].events[0]",
        "event_rules[0]"
      ]
 */
export const sortEventRuleChangesByPathReverse = (
  changeA: { path?: string },
  changeB: { path?: string }
): number => {
  // Handle cases where one or both objects don't have valid paths
  const hasValidPathA = changeA?.path && changeA.path.trim() !== "";
  const hasValidPathB = changeB?.path && changeB.path.trim() !== "";

  if (!hasValidPathA && !hasValidPathB) {
    return 0; // Both invalid, maintain original order
  }
  if (!hasValidPathA) {
    return 1; // A is invalid, B comes first
  }
  if (!hasValidPathB) {
    return -1; // B is invalid, A comes first
  }

  // Extract numeric indices for proper sorting
  const getPathSortKey = (path: string) => {
    const eventRuleIndex = getEventRuleIndex(path);
    const ruleIndex = getEventRuleRuleIndex(path);
    const eventIndex = getEventRuleEventIndex(path);

    // Create a sort key that considers all levels of nesting
    // Format: [eventRuleIndex, ruleIndex, eventIndex] (with -1 for missing indices)
    return [eventRuleIndex ?? -1, ruleIndex ?? -1, eventIndex ?? -1];
  };

  const keyA = getPathSortKey(changeA.path ?? "");
  const keyB = getPathSortKey(changeB.path ?? "");

  // Compare arrays element by element for proper hierarchical sorting
  for (let i = 0; i < 3; i++) {
    const a = keyA[i];
    const b = keyB[i];
    if (a !== b) {
      // Reverse order (higher indices first) for deletion from end
      return b - a;
    }
  }

  return 0;
};
